'use client'

import {
  useEffect,
  useRef,
  useState,
  FC,
  HTMLProps,
  JSX,
  Ref,
  ChangeEvent,
  ReactNode
} from 'react'
import { ApolloError } from '@apollo/client'
import {
  autoUpdate,
  useDismiss,
  useFloating,
  useId,
  useInteractions,
  useListNavigation,
  useMergeRefs,
  useRole
} from '@floating-ui/react'

import {
  Button,
  Callout,
  CalloutText,
  Input,
  InputWrapper,
  Skeleton,
  Stack,
  cn
} from 'design-system'

import { Check, Search } from 'lucide-react'

interface FilteredListItemProps extends HTMLProps<HTMLDivElement> {
  children: ReactNode
  active: boolean
  selected: boolean
  ref?: Ref<HTMLDivElement>
}

const FilteredListItem: FC<FilteredListItemProps> = ({
  children,
  active,
  selected,
  ref,
  ...rest
}) => {
  const id = useId()
  return (
    <div
      ref={ref}
      role="option"
      id={id}
      aria-selected={active}
      {...rest}
      className={cn(
        'relative flex shrink-0 cursor-pointer select-none items-center justify-between gap-x-2 rounded-lg p-2 text-sm outline-none transition-colors hover:bg-blue-100 focus:bg-blue-100 focus:outline-none',
        {
          'bg-gray-100': selected,
          'bg-blue-100': active
        }
      )}>
      <div className="flex grow items-center gap-x-2 truncate">{children}</div>
      {selected ? <Check size={16} aria-hidden className="shrink-0" /> : null}
    </div>
  )
}

type FilteredListProps<T> = {
  items: T[]
  error?: ApolloError
  loading: boolean
  onSelect(item: T): void
  filterPredicate(item: T, inputValue: string): boolean
  placeholder: string
  renderItem(item: T): JSX.Element
  itemsName: string
  selectedId?: string
  onSearch?(query: string): void
  fetchMore?(): void
  inputAutofocus?: boolean
  ariaLabel?: string
}

export function FilteredList<ItemType extends { id: string }>({
  items,
  error,
  loading,
  onSelect,
  filterPredicate,
  placeholder,
  renderItem,
  selectedId,
  itemsName,
  onSearch,
  fetchMore,
  inputAutofocus = true,
  ariaLabel
}: FilteredListProps<ItemType>) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [inputValue, setInputValue] = useState('')
  const [activeIndex, setActiveIndex] = useState<number | null>(null)

  const listRef = useRef<Array<HTMLElement | null>>([])

  const { refs, context } = useFloating<HTMLInputElement>({
    whileElementsMounted: autoUpdate,
    open: true
  })

  const listboxId = useId()

  const onSetActiveIndex = (index: number | null) => {
    // Button is a part of the list, as last item by default floating-list is not focusing it
    // We need to transfer focus to support Enter key press on Load more button
    // Also we want to focus input if we press up/down arrow to leave Button
    if (fetchMore) {
      if (index === items.length) {
        listRef.current[index]?.focus()
      } else {
        inputRef.current?.focus()
      }
    }

    setActiveIndex(index)
  }

  const role = useRole(context, { role: 'listbox' })
  const dismiss = useDismiss(context)
  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: onSetActiveIndex,
    virtual: true,
    loop: true
  })

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [role, dismiss, listNav]
  )

  function onChange(event: ChangeEvent<HTMLInputElement>) {
    const value = event.target.value
    setInputValue(value)

    if (value) {
      setActiveIndex(0)
    }

    if (onSearch) {
      onSearch(value)
    }
  }
  const ref = useMergeRefs([refs.setReference, inputRef])

  useEffect(() => {
    inputRef?.current?.focus()
    // Wait for async lists to load before selecting
    setTimeout(() => setActiveIndex(0), 30)
  }, [loading])

  if (error) {
    return (
      <div>Sorry we can&apos;t load {itemsName}, try to refresh the page</div>
    )
  }

  const filteredItems = items.filter(item => filterPredicate(item, inputValue))
  const ServiceScreen = loading ? (
    <Stack gap={2}>
      <Skeleton className="h-9 rounded-lg" />
      <Skeleton className="h-9 rounded-lg" />
      <Skeleton className="h-9 rounded-lg" />
    </Stack>
  ) : (
    <Callout calloutSize="sm">
      <CalloutText calloutSize="sm">No {itemsName} found</CalloutText>
    </Callout>
  )

  return (
    <Stack gap={4} className="p-2">
      <InputWrapper leftIconSlot={<Search />}>
        <Input
          {...getReferenceProps({
            ref,
            onChange,
            value: inputValue,
            type: 'text',
            placeholder,
            autoFocus: inputAutofocus,
            role: 'combobox',
            disabled: loading,
            onKeyDown(event) {
              if (
                event.key === 'Enter' &&
                activeIndex !== null &&
                filteredItems[activeIndex]
              ) {
                if (filteredItems[activeIndex].id !== selectedId && !loading) {
                  onSelect(filteredItems[activeIndex])
                }
              }
            }
          })}
          aria-expanded={filteredItems.length > 0}
          aria-controls={listboxId}
          aria-label={ariaLabel}
        />
      </InputWrapper>
      <div
        {...getFloatingProps({
          ref: refs.setFloating
        })}
        id={listboxId}
        className="flex max-h-48 w-full flex-col gap-y-2 overflow-y-auto">
        {filteredItems.map((item, index) => (
          <FilteredListItem
            key={`filtered-item-${item.id}`}
            {...getItemProps({
              ref(node) {
                listRef.current[index] = node
              },
              onClick() {
                if (item.id !== selectedId) {
                  onSelect(item)
                }

                refs.domReference.current?.focus()
              }
            })}
            active={activeIndex === index}
            selected={item.id === selectedId}>
            {renderItem(item)}
          </FilteredListItem>
        ))}
      </div>
      {filteredItems.length === 0 ? ServiceScreen : null}
      {filteredItems.length > 0 && !loading && fetchMore ? (
        <Button
          buttonSize="sm"
          {...getItemProps({
            ref(node) {
              listRef.current[filteredItems.length] = node
            },
            onClick() {
              fetchMore()
              // Manually set focus and active index because fetchMore not triggering loading

              inputRef.current?.focus()
              // Set last item before button active
              setActiveIndex(items.length - 1)
            }
          })}
          className="shrink-0">
          Load more
        </Button>
      ) : null}
    </Stack>
  )
}
